import { useCallback, useEffect, useState } from 'react';
import type { UseFormTrigger, UseFormWatch } from 'react-hook-form';
import type { ZodType } from 'zod';

import type {
  AdminTypeValue,
  CreateOrganizationForm
} from '@/features/profile-setting/schemas/createOrganization';
import { AdminType } from '@/features/profile-setting/schemas/createOrganization';

const EMPTY_FORM: CreateOrganizationForm = {
  name: '',
  adminEmail: '',
  tags: [],
  geoAreaIds: [],
  adminType: AdminType.SELF,
  data: {}
};

/**
 * Returns the form fields that belong to the given wizard step and therefore
 * need to be validated before advancing. Step 0 always validates the
 * organization data; step 1 only validates the admin email when an external
 * admin is chosen.
 */
export const getStepFields = (
  step: number,
  adminType: AdminTypeValue
): (keyof CreateOrganizationForm)[] => {
  if (step === 0) {
    return ['name', 'tags', 'geoAreaIds', 'data'];
  }
  if (step === 1 && adminType === AdminType.OTHER) {
    return ['adminEmail'];
  }
  return [];
};

type Params = {
  schema: ZodType<CreateOrganizationForm>;
  trigger: UseFormTrigger<CreateOrganizationForm>;
  watch: UseFormWatch<CreateOrganizationForm>;
  adminType: AdminTypeValue;
};

/**
 * Drives the organization-creation wizard: current step, per-step validity
 * (used to enable the footer button) and forward/back navigation.
 */
export const useCreateOrganizationWizard = ({
  schema,
  trigger,
  watch,
  adminType
}: Params) => {
  const [step, setStep] = useState<number>(0);
  const [isStepValid, setIsStepValid] = useState<boolean>(false);

  const validateStep = useCallback(
    (values: Partial<CreateOrganizationForm>): boolean => {
      const merged = { ...EMPTY_FORM, ...values };
      const result = schema.safeParse(merged);
      if (result.success) return true;

      const stepFields = getStepFields(step, merged.adminType);
      if (stepFields.length === 0) return true;

      const errorFields = result.error.issues
        .map((issue) => issue.path[0])
        .filter((field): field is keyof CreateOrganizationForm =>
          stepFields.includes(field as keyof CreateOrganizationForm)
        );
      return errorFields.length === 0;
    },
    [schema, step]
  );

  useEffect(() => {
    setIsStepValid(validateStep(watch()));
    const subscription = watch((values) => {
      setIsStepValid(validateStep(values as Partial<CreateOrganizationForm>));
    });
    return () => subscription.unsubscribe();
  }, [step, validateStep, watch]);

  const goToNextStep = useCallback(async () => {
    const fieldsToValidate = getStepFields(step, adminType);
    if (fieldsToValidate.length === 0) {
      setStep((current) => current + 1);
      return;
    }
    const isValid = await trigger(fieldsToValidate);
    if (isValid) setStep((current) => current + 1);
  }, [step, trigger, adminType]);

  const goToPreviousStep = useCallback(() => {
    setStep((current) => Math.max(0, current - 1));
  }, []);

  const resetSteps = useCallback(() => {
    setStep(0);
    setIsStepValid(false);
  }, []);

  return {
    step,
    isStepValid,
    goToNextStep,
    goToPreviousStep,
    resetSteps
  };
};

export const DEFAULT_FORM_VALUES = EMPTY_FORM;
